/**
 * Spanish (US) locale for phone-numbers page.
 * Export is the "phoneNumbers" subtree for host i18n merge.
 */
import type { PhoneNumbersLocale } from './en'

export const phoneNumbersEsUs = {
  list: {
    title: `Números de teléfono`,
    serverGroup: `Grupo de servidores:`,
    searchPlaceholder: `Buscar...`,
    loading: `Cargando números de teléfono...`,
    error: `Error al cargar los números de teléfono`,
    empty: `No se encontraron números de teléfono para esta central`,
    noResults: `Ningún número de teléfono coincide con su búsqueda`,
    columnName: `Nombre`,
    columnNumber: `Número`,
    columnExtension: `Extensión`,
    columnCallerId: `ID de llamante`,
    columnType: `Tipo`,
    columnTollFree: `Gratuito`,
    columnSms: `SMS`,
    columnE911: `e911`,
    columnVoiceProvider: `Proveedor de voz`,
    columnSmsProvider: `Proveedor de SMS`,
    columnProviders: `Proveedores`,
    columnStatus: `Estado`,
    columnUpdated: `Actualizado`,
    yes: `Sí`,
    no: `No`,
    smsActive: `Activo`,
    e911Verified: `Verificado`,
    createButton: `+ Crear número de teléfono`,
    createDisabledTooltip: `La creación de números es gestionada por el equipo de telefonía`,
    filterButton: `Filtros`,
    copyNumber: `Copiar número`,
    toast: {
      createSuccess: `Número de teléfono creado`,
      createFailed: `Error al crear el número de teléfono`,
      copied: `Número copiado al portapapeles`,
      copyFailed: `Error al copiar el número`,
    },
  },
  filters: {
    status: `Estado`,
    allStatuses: `Todos`,
    type: `Tipo`,
    allTypes: `Todos`,
    voiceProvider: `Proveedor de voz`,
    smsProvider: `Proveedor de SMS`,
    allProviders: `Todos`,
    tollFree: `Gratuito`,
    any: `Cualquiera`,
    showDeleted: `Mostrar eliminados`,
    clearAll: `Borrar filtros`,
  },
  types: {
    callerId: `ID de llamada`,
    smsOnly: `Solo SMS`,
    standard: `Estándar`,
    temporary: `Temporal`,
  },
  bulk: {
    title: `Actualización masiva de estado`,
    description: `Actualiza el estado de los números de teléfono seleccionados.`,
    selected: `{{count}} seleccionados`,
    apply: `Aplicar`,
    updateStatus: `Actualizar estado`,
    clearSelection: `Limpiar`,
    maxExceeded: `Máximo 25 a la vez`,
    selectAll: `Seleccionar todos los números de teléfono`,
    selectRow: `Seleccionar {{number}}`,
    toast: {
      success: `Estado actualizado exitosamente`,
      failed: `Error al actualizar el estado en masa`,
    },
    results: {
      titleSuccess: `Actualización masiva completada`,
      titleAllFailed: `Error en la actualización masiva`,
      titlePartial: `Actualización masiva completada parcialmente`,
      summarySuccess: `Se actualizaron exitosamente {{count}} número(s) de teléfono.`,
      summaryAllFailed: `Los {{count}} número(s) de teléfono no se pudieron actualizar.`,
      summaryPartial: `{{successCount}} de {{totalCount}} actualizados. {{failedCount}} fallaron:`,
      colNumber: `Número`,
      colStatus: `Estado actual`,
      colError: `Error`,
      close: `Cerrar`,
    },
  },
  detail: {
    loading: `Cargando número de teléfono...`,
    error: `Error al cargar el número de teléfono`,
    backToList: `Volver a la lista`,
    editButton: `Editar número de teléfono`,
    deleteTitle: `Eliminar número de teléfono`,
    deleteButton: `Eliminar`,
    deleteConfirmation: `Esto eliminará temporalmente el número de teléfono. Se puede restaurar más tarde si es necesario. ¿Está seguro?`,
    restoreButton: `Restaurar`,
    noopSaveButton: `Re-guardar`,
    deleted: `Eliminado`,
    sectionGeneral: `Información general`,
    sectionProviders: `Proveedores`,
    sectionRouting: `Enrutamiento`,
    sectionSms: `SMS`,
    sectionE911: `Información E911`,
    sectionTimestamps: `Marcas de tiempo`,
    sectionSmsDiagnostics: `Diagnósticos de SMS Telnyx`,
    diagnosticWarning: `Advertencia`,
    fieldName: `Nombre`,
    fieldNumber: `Número`,
    fieldPbx: `Central`,
    fieldType: `Tipo`,
    fieldCallerId: `ID de llamante`,
    fieldExtension: `Extensión`,
    fieldTollFree: `Gratuito`,
    fieldCountryCode: `Código de país`,
    fieldLocalPresence: `Presencia local`,
    fieldCampaignId: `ID de campaña`,
    fieldVoiceProvider: `Proveedor de voz`,
    fieldSmsProvider: `Proveedor de SMS`,
    fieldSmsEnabled: `SMS habilitado`,
    fieldSmsStatus: `Estado de SMS`,
    fieldSmsRelay: `Retransmisión SMS`,
    fieldSmsRelayIntegration: `Integración de retransmisión SMS`,
    fieldSmsGroup: `Grupo SMS`,
    fieldE911Status: `Estado E911`,
    fieldE911Address: `Dirección`,
    fieldE911Address2: `Dirección 2`,
    fieldE911City: `Ciudad`,
    fieldE911State: `Estado`,
    fieldSendFaxesFrom: `Enviar faxes desde`,
    usePbxDefault: `Usar predeterminado de PBX`,
    fieldPbxServerGroup: `Grupo de servidor PBX`,
    fieldSourceTracking: `Seguimiento de origen`,
    fieldStatus: `Estado`,
    fieldE911PostalCode: `Código postal`,
    fieldCreatedAt: `Creado`,
    fieldUpdatedAt: `Actualizado`,
    viewInProvider: `Ver en {{provider}}`,
    sectionMetrics: `Métricas`,
    fieldCallStats: `Estadísticas de llamadas`,
    callStatsLink: `Estadísticas de llamadas`,
    on: `Encendido`,
    off: `Desactivado`,
    registered: `Registrado`,
    inactive: `Inactivo`,
    notRegistered: `No registrado`,
    toast: {
      updateSuccess: `Número de teléfono actualizado`,
      updateFailed: `Error al actualizar el número de teléfono`,
      deleteSuccess: `Número de teléfono eliminado`,
      deleteFailed: `Error al eliminar el número de teléfono`,
      restoreSuccess: `Número de teléfono restaurado`,
      restoreFailed: `Error al restaurar el número de teléfono`,
      noopSaveSuccess: `Se volvieron a activar los efectos secundarios`,
      noopSaveFailed: `Error al volver a guardar`,
    },
  },
  edit: {
    title: `Editar número de teléfono`,
    numberLabel: `Número`,
    nameLabel: `Nombre`,
    namePlaceholder: `Nombre del número de teléfono`,
    typeLabel: `Tipo`,
    statusLabel: `Estado`,
    selectStatus: `— Seleccionar estado —`,
    voiceProviderLabel: `Proveedor de voz`,
    smsProviderLabel: `Proveedor de SMS`,
    selectProvider: `— Seleccionar —`,
    unassigned: `Sin asignar`,
    callerIdLabel: `ID de llamante`,
    sendFaxesFromLabel: `Enviar faxes desde`,
    usePbxDefault: `Usar predeterminado de PBX`,
    extensionLabel: `Extensión`,
    extensionPlaceholder: `UUID de extensión`,
    extensionHelp: `Extensión opcional para el número de teléfono.`,
    noExtension: `Ninguna`,
    sourceTrackingLabel: `Seguimiento de origen`,
    sourceTrackingHelp: `Anteponer hasta 6 caracteres al ID de llamante para llamadas entrantes. Esto se usa comúnmente para rastrear campañas publicitarias.`,
    localPresenceLabel: `Presencia local`,
    localPresenceHelp: `Los grupos de presencia local crean un conjunto de números desde los cuales una extensión de línea con presencia local habilitada puede realizar llamadas para aparecer como local ante el destinatario.`,
    e911Section: `Configuración de emergencia 911 (E911)`,
    e911AddressLabel: `Dirección`,
    e911Address2Label: `Apartamento/suite`,
    e911CityLabel: `Ciudad`,
    e911StateLabel: `Estado/Provincia`,
    e911PostalCodeLabel: `Código postal`,
    e911Disclaimer: `E911 es un sistema utilizado en Norteamérica para proporcionar automáticamente la ubicación del llamante a los despachadores del 911. Asegúrese de que esta información sea precisa; la vida de alguien podría depender de ello.`,
    smsSection: `Configuración de SMS`,
    smsEnabledLabel: `SMS habilitado`,
    smsEnabledWarning: `Advertencia: habilitar SMS desencadenará una solicitud de portación del eSPID en este número, lo que puede causar interrupciones de SMS para el cliente. Por favor, asegúrese de que esta es la acción deseada antes de continuar.`,
    smsRelayLabel: `Retransmisión SMS`,
    smsRelayIntegrationLabel: `Integración de retransmisión SMS`,
    smsRelayIntegrationDisabledHelp: `Active la retransmisión SMS para elegir una integración.`,
    noIntegration: `Ninguna`,
    integrationSearchPlaceholder: `Buscar integraciones...`,
    integrationSearchAriaLabel: `Buscar integraciones de retransmisión SMS por nombre`,
    noIntegrationMatches: `Ninguna integración coincide con esa búsqueda.`,
    campaignIdLabel: `ID de campaña`,
    campaignIdHelp: `Requerido para enviar SMS. Antes de establecer este valor, asegúrese de que un ID de campaña esté vinculado a este número en el proveedor correspondiente. No hacerlo puede causar interrupciones del servicio, fallos de entrega y/o multas.`,
    titleWithNumber: `Editar número de teléfono — {{number}}`,
    advancedSection: `Avanzado`,
    e911NotConfigured: `No configurado`,
    extensionSearchPlaceholder: `Buscar extensiones...`,
    extensionSearchAriaLabel: `Buscar extensiones por nombre o número`,
    noExtensionMatches: `Ninguna extensión coincide con esa búsqueda.`,
    cancel: `Cancelar`,
    save: `Guardar`,
  },
  create: {
    title: `Crear número de teléfono`,
    numberLabel: `Número de teléfono`,
    numberPlaceholder: `+1234567890`,
    numberHelp: `Debe incluir código de país (ej. +18015551234 para EE. UU., +442079460958 para Reino Unido).`,
    numberHelpWithPrefix: `Ingrese el número sin el código de país.`,
    selectType: `— Seleccionar tipo —`,
    saveButton: `Crear`,
    createMore: `Crear más`,
    searchStateProvince: `Buscar...`,
    noStateProvinceMatches: `Sin resultados`,
  },
  validation: {
    numberRequired: `El número de teléfono es obligatorio`,
    numberInvalid: `Ingrese un número de teléfono válido (ej. +1 (801) 555-1234)`,
    numberInvalidNational: `Ingrese un número de teléfono válido (ej. (801) 555-1234)`,
    numberMissingCountryCode: `El número de teléfono debe incluir un código de país (ej. +1 para EE. UU., +44 para Reino Unido)`,
    nameRequired: `El nombre es obligatorio`,
    typeRequired: `El tipo es obligatorio`,
    statusRequired: `El estado es obligatorio`,
    voiceProviderRequired: `El proveedor de voz es obligatorio`,
    callerIdTooLong: `Máximo 15 caracteres`,
    callerIdInvalidChars: `Solo se permiten letras, números y espacios`,
    callerIdAllDigits: `No puede ser solo dígitos`,
    campaignIdFormat: `Debe ser exactamente 7 caracteres alfanuméricos`,
    sourceTrackingFormat: `Solo 1-6 caracteres alfanuméricos`,
    e911Incomplete: `Dirección, ciudad, estado y código postal son obligatorios cuando se completa cualquier campo E911`,
  },
  pagination: {
    resultsPerPage: `Resultados por página:`,
    previousPage: `Página anterior`,
    nextPage: `Página siguiente`,
    jumpToPage: `Ir a la página {{page}}`,
    all: `Todos`,
    recordRange: `{{start}}-{{end}} de {{total}}`,
    recordRangeEmpty: `0 de 0`,
  },
} satisfies PhoneNumbersLocale
